import type { StoreApi } from "zustand"

import { getRouteApi } from "@tanstack/react-router"
import { useStore } from "zustand"
import { createStore } from "zustand/vanilla"

import type { AppToast } from "@/components/shared/toast-stack"
import type { GuildWarCandidatePoolMember } from "@/core/guild-war/types"
import type { GuildWarRosterBoardRecord } from "@/lib/guild-war-api"
import type {
  TeamBoardSlotTarget,
  TeamBoardState,
  TeamSubteam,
} from "@/lib/team-board-store"

import {
  applySaveFailure as applyCoordinatorSaveFailure,
  applySaveSuccess as applyCoordinatorSaveSuccess,
  beginDraftSave,
  createDraftSnapshot,
  createRosterCoordinatorState,
  type SaveStatus,
} from "@/components/guild-war/team-board-roster-coordinator"
import {
  clearGuildWarRosterBoard,
  createEmptyGuildWarRosterBoard,
  getGuildWarRosterFormat,
  hasGuildWarViceCommanderSlot,
  isGuildWarFlatTeamRoster,
  type GuildWarRosterFormatChange,
} from "@/core/guild-war/roster-board"
import { pruneUnassignedOverrides } from "@/core/guild-war/roster-board-helpers"
import {
  coerceTeamBoardState,
  emptyBoard,
  getAssignedMemberIds,
  getMemberIdForTarget,
  removeFromAllSlots,
} from "@/lib/team-board-store"
import { type ActionProps, extractActions } from "@/lib/zustand-helpers"

const TEAM_TABS = [
  "all",
  "mobility",
  "attack",
  "defense",
  "commanders",
  "halftime",
] as const

type TeamBoardActiveTab = (typeof TEAM_TABS)[number]

type TeamBoardPageState = {
  rosterDate: string
  board: TeamBoardState

  selectedTarget: TeamBoardSlotTarget | null
  draggingMemberId: string | null

  search: string
  permanentFilter: "all" | GuildWarCandidatePoolMember["permanentStatus"]
  availabilityFilter: "all" | GuildWarCandidatePoolMember["availability"]
  pathFilter: string

  activeTab: TeamBoardActiveTab
  showInsights: boolean
  showExportPreview: boolean
  isExporting: boolean

  sourceWeekId: string
  effectiveSourceWeekId: string
  exportDateLabel: string

  saveStatus: SaveStatus
  saveMessage: string | null
  publishMessage: string | null
  hasPendingSave: boolean
  currentVersion: number
  lastSavedSnapshot: string | null

  rosterRecord: GuildWarRosterBoardRecord | null
  loadedRosterDate: string | null
  rosterLoadError: string | null

  toasts: AppToast[]

  setRosterDate: (value: string) => void
  setBoard: (value: TeamBoardState) => void
  assignToSlot: (memberId: string, target: TeamBoardSlotTarget) => boolean
  removeFromBoard: (memberId: string) => void
  clearCommanderSlot: (index: number) => void
  clearHalftimeSlot: (index: number) => void
  clearBoard: () => void
  changeRosterFormat: (change: GuildWarRosterFormatChange) => boolean
  setMemberPathSnapshot: (
    memberId: string,
    snapshot: TeamBoardState["memberPaths"][string],
  ) => void
  clearMemberPathSnapshot: (memberId: string) => void
  setMemberDisplayName: (memberId: string, name: string) => void
  clearMemberDisplayName: (memberId: string) => void

  setSelectedTarget: (value: TeamBoardSlotTarget | null) => void
  clearSelectedTarget: () => void
  setDraggingMemberId: (value: string | null) => void
  setSearch: (value: string) => void
  setPermanentFilter: (
    value: "all" | GuildWarCandidatePoolMember["permanentStatus"],
  ) => void
  setAvailabilityFilter: (
    value: "all" | GuildWarCandidatePoolMember["availability"],
  ) => void
  setPathFilter: (value: string) => void
  resetFilters: () => void
  setActiveTab: (value: TeamBoardActiveTab) => void
  setShowInsights: (value: boolean) => void
  setShowExportPreview: (value: boolean) => void
  setIsExporting: (value: boolean) => void

  setSourceWeekId: (value: string) => void
  setEffectiveSourceWeekId: (value: string) => void
  setExportDateLabel: (value: string) => void
  beginSave: () => void
  applySaveSuccess: (
    record: GuildWarRosterBoardRecord,
    snapshot: string,
    input?: { successMessage?: string | null },
  ) => void
  applySaveFailure: (input: { code?: string; message: string }) => void
  setPublishMessage: (value: string | null) => void
  setSaveFeedback: (status: SaveStatus, message: string | null) => void
  setRosterLoadError: (value: string | null) => void
  resetForNoRosterDate: () => void
  initializeMissingWeekDraft: (rosterDate: string) => void
  hydrateFromAuthoritativeRecord: (input: {
    record: GuildWarRosterBoardRecord
    rosterDate: string
    preserveFeedback?: boolean
  }) => void
  loadPublishedIntoDraft: () => void

  pushToast: (toast: Omit<AppToast, "id">) => number
  dismissToast: (toastId: number) => void
  clearToasts: () => void
  dispose: () => void
}

export type TeamBoardPageActions = ActionProps<TeamBoardPageState>
export type TeamBoardPageData = Omit<
  TeamBoardPageState,
  keyof TeamBoardPageActions
>

export type TeamBoardPageStore = ReturnType<typeof createTeamBoardPageStore>

const cloneBoard = (board: TeamBoardState): TeamBoardState =>
  structuredClone(board)

const getSubteamIndex = (squad: "alpha" | "beta") => (squad === "alpha" ? 0 : 1)

const repairActiveTab = (
  board: TeamBoardState,
  tab: TeamBoardActiveTab,
): TeamBoardActiveTab =>
  (tab === "mobility" || tab === "attack" || tab === "defense") &&
  !Object.hasOwn(board, tab)
    ? "all"
    : tab

export const createTeamBoardPageStore = () => {
  const toastTimeouts: Record<number, ReturnType<typeof setTimeout>> = {}

  const clearToastTimeout = (toastId: number) => {
    const timeout = toastTimeouts[toastId]
    if (timeout) {
      clearTimeout(timeout)
      delete toastTimeouts[toastId]
    }
  }

  const store = createStore<TeamBoardPageState>((set, get) => ({
    rosterDate: "",
    board: cloneBoard(emptyBoard),

    selectedTarget: null,
    draggingMemberId: null,

    search: "",
    permanentFilter: "all",
    availabilityFilter: "all",
    pathFilter: "all",

    activeTab: "all",
    showInsights: false,
    showExportPreview: false,
    isExporting: false,

    sourceWeekId: "",
    effectiveSourceWeekId: "",
    exportDateLabel: "",

    saveStatus: "idle",
    saveMessage: null,
    publishMessage: null,
    hasPendingSave: false,
    currentVersion: 0,
    lastSavedSnapshot: null,

    rosterRecord: null,
    loadedRosterDate: null,
    rosterLoadError: null,

    toasts: [],

    setRosterDate: (value) =>
      set((state) => ({
        rosterDate: value,
        selectedTarget: null,
        hasPendingSave:
          value === state.rosterDate ? state.hasPendingSave : false,
      })),
    setBoard: (value) =>
      set((state) => {
        const board = cloneBoard(value)
        return {
          board,
          activeTab: repairActiveTab(board, state.activeTab),
          selectedTarget: null,
        }
      }),
    assignToSlot: (memberId, target) => {
      let accepted = false
      set((state) => {
        const team =
          target.kind === "team-role" ||
          target.kind === "team-member" ||
          target.kind === "subteam-member"
            ? Object.hasOwn(state.board, target.team)
              ? state.board[target.team]!
              : null
            : null
        const validTarget =
          target.kind === "halftime"
            ? Number.isInteger(target.index) &&
              target.index >= 0 &&
              target.index < state.board.halftimePerformers.length
            : target.kind === "commander-overlay"
              ? Number.isInteger(target.index) &&
                target.index >= 0 &&
                target.index < state.board.commanders.length
              : target.kind === "team-role"
                ? team !== null &&
                  (target.role === "commander" ||
                    (target.role === "viceCommander" &&
                      hasGuildWarViceCommanderSlot(team)))
                : target.kind === "team-member"
                  ? team !== null &&
                    isGuildWarFlatTeamRoster(team) &&
                    Number.isInteger(target.index) &&
                    target.index >= 0 &&
                    target.index < team.members.length
                  : team !== null &&
                    !isGuildWarFlatTeamRoster(team) &&
                    Number.isInteger(target.index) &&
                    target.index >= 0 &&
                    target.index <
                      team.subteams[getSubteamIndex(target.squad)].members
                        .length

        if (!validTarget) return {}
        accepted = true

        let baseBoard = state.board
        if (target.kind !== "halftime" && target.kind !== "commander-overlay") {
          baseBoard = removeFromAllSlots(state.board, memberId)
        }

        if (target.kind === "team-role") {
          const team = baseBoard[target.team]!
          return {
            board: {
              ...baseBoard,
              [target.team]: {
                ...team,
                [target.role]: memberId,
              },
            },
          }
        }

        if (target.kind === "team-member") {
          const team = baseBoard[target.team]!
          if (!isGuildWarFlatTeamRoster(team)) return {}
          return {
            board: {
              ...baseBoard,
              [target.team]: {
                ...team,
                members: team.members.map((member, index) =>
                  index === target.index ? memberId : member,
                ),
              },
            },
          }
        }

        if (target.kind === "subteam-member") {
          const team = baseBoard[target.team]!
          const subteamIndex = getSubteamIndex(target.squad)
          if (isGuildWarFlatTeamRoster(team)) return {}

          return {
            board: {
              ...baseBoard,
              [target.team]: {
                ...team,
                subteams: team.subteams.map((subteam, index) => {
                  if (index !== subteamIndex) {
                    return subteam
                  }

                  return {
                    ...subteam,
                    members: subteam.members.map((member, memberIndex) =>
                      memberIndex === target.index ? memberId : member,
                    ),
                  }
                }) as [TeamSubteam, TeamSubteam],
              },
            },
          }
        }

        if (target.kind === "halftime") {
          return {
            board: {
              ...baseBoard,
              halftimePerformers: baseBoard.halftimePerformers.map(
                (value, index) => {
                  if (index === target.index) {
                    return memberId
                  }

                  return value === memberId ? null : value
                },
              ) as TeamBoardState["halftimePerformers"],
            },
          }
        }

        if (target.kind === "commander-overlay") {
          return {
            board: {
              ...baseBoard,
              commanders: baseBoard.commanders.map((value, index) => {
                if (index === target.index) {
                  return memberId
                }

                return value === memberId ? null : value
              }) as TeamBoardState["commanders"],
            },
          }
        }

        return { board: baseBoard }
      })
      return accepted
    },
    removeFromBoard: (memberId) =>
      set((state) => ({
        board: pruneUnassignedOverrides(
          removeFromAllSlots(state.board, memberId),
        ),
      })),
    clearCommanderSlot: (index) =>
      set((state) => ({
        board: pruneUnassignedOverrides({
          ...state.board,
          commanders: state.board.commanders.map((value, currentIndex) =>
            currentIndex === index ? null : value,
          ) as TeamBoardState["commanders"],
        }),
      })),
    clearHalftimeSlot: (index) =>
      set((state) => ({
        board: pruneUnassignedOverrides({
          ...state.board,
          halftimePerformers: state.board.halftimePerformers.map(
            (value, currentIndex) => (currentIndex === index ? null : value),
          ) as TeamBoardState["halftimePerformers"],
        }),
      })),
    clearBoard: () =>
      set((state) => {
        const board = clearGuildWarRosterBoard(state.board)
        return {
          board,
          activeTab: repairActiveTab(board, state.activeTab),
          selectedTarget: null,
          draggingMemberId: null,
        }
      }),
    changeRosterFormat: (change) => {
      const state = get()
      if (
        state.board !== change.sourceBoard ||
        getGuildWarRosterFormat(state.board) !== change.sourceFormat ||
        getGuildWarRosterFormat(change.convertedBoard) !== change.targetFormat
      ) {
        return false
      }

      set({
        board: change.convertedBoard,
        activeTab: repairActiveTab(change.convertedBoard, state.activeTab),
        selectedTarget: null,
        draggingMemberId: null,
      })
      return true
    },
    setMemberPathSnapshot: (memberId, snapshot) =>
      set((state) => ({
        board: {
          ...state.board,
          memberPaths: {
            ...state.board.memberPaths,
            [memberId]: {
              primaryPathCode: snapshot.primaryPathCode,
              secondaryPathCode: snapshot.secondaryPathCode,
            },
          },
        },
      })),
    clearMemberPathSnapshot: (memberId) =>
      set((state) => {
        const { [memberId]: _removed, ...remaining } = state.board.memberPaths
        return {
          board: {
            ...state.board,
            memberPaths: remaining,
          },
        }
      }),
    setMemberDisplayName: (memberId, name) =>
      set((state) => {
        const trimmed = name.trim()
        if (!trimmed) {
          const { [memberId]: _, ...rest } = state.board.memberDisplayNames
          return {
            board: {
              ...state.board,
              memberDisplayNames: rest,
            },
          }
        }

        return {
          board: {
            ...state.board,
            memberDisplayNames: {
              ...state.board.memberDisplayNames,
              [memberId]: trimmed,
            },
          },
        }
      }),
    clearMemberDisplayName: (memberId) =>
      set((state) => {
        const { [memberId]: _, ...rest } = state.board.memberDisplayNames
        return {
          board: {
            ...state.board,
            memberDisplayNames: rest,
          },
        }
      }),

    setSelectedTarget: (value) => set({ selectedTarget: value }),
    clearSelectedTarget: () => set({ selectedTarget: null }),
    setDraggingMemberId: (value) => set({ draggingMemberId: value }),
    setSearch: (value) => set({ search: value }),
    setPermanentFilter: (value) => set({ permanentFilter: value }),
    setAvailabilityFilter: (value) => set({ availabilityFilter: value }),
    setPathFilter: (value) => set({ pathFilter: value }),
    resetFilters: () =>
      set({
        search: "",
        permanentFilter: "all",
        availabilityFilter: "all",
        pathFilter: "all",
      }),
    setActiveTab: (value) =>
      set((state) => ({ activeTab: repairActiveTab(state.board, value) })),
    setShowInsights: (value) => set({ showInsights: value }),
    setShowExportPreview: (value) => set({ showExportPreview: value }),
    setIsExporting: (value) => set({ isExporting: value }),

    setSourceWeekId: (value) => set({ sourceWeekId: value }),
    setEffectiveSourceWeekId: (value) => set({ effectiveSourceWeekId: value }),
    setExportDateLabel: (value) => set({ exportDateLabel: value }),
    beginSave: () =>
      set((state) => {
        const next = beginDraftSave(
          createRosterCoordinatorState({
            currentVersion: state.currentVersion,
            lastSavedSnapshot: state.lastSavedSnapshot,
          }),
        )

        return {
          hasPendingSave: next.hasPendingSave,
          saveStatus: next.saveStatus,
          saveMessage: next.saveMessage,
        }
      }),
    applySaveSuccess: (record, snapshot, input) =>
      set((state) => {
        const next = applyCoordinatorSaveSuccess(
          createRosterCoordinatorState({
            currentVersion: state.currentVersion,
            lastSavedSnapshot: state.lastSavedSnapshot,
          }),
          {
            record,
            snapshot,
            successMessage: input?.successMessage,
          },
        )

        return {
          currentVersion: next.currentVersion,
          lastSavedSnapshot: next.lastSavedSnapshot,
          hasPendingSave: next.hasPendingSave,
          saveStatus: next.saveStatus,
          saveMessage: next.saveMessage,
        }
      }),
    applySaveFailure: (input) =>
      set((state) => {
        const next = applyCoordinatorSaveFailure(
          createRosterCoordinatorState({
            currentVersion: state.currentVersion,
            lastSavedSnapshot: state.lastSavedSnapshot,
          }),
          input,
        )

        return {
          hasPendingSave: next.hasPendingSave,
          saveStatus: next.saveStatus,
          saveMessage: next.saveMessage,
        }
      }),
    setPublishMessage: (value) => set({ publishMessage: value }),
    setSaveFeedback: (status, message) =>
      set({
        saveStatus: status,
        saveMessage: message,
      }),
    setRosterLoadError: (value) => set({ rosterLoadError: value }),
    resetForNoRosterDate: () =>
      set((state) => {
        const board = createEmptyGuildWarRosterBoard()
        return {
          board,
          activeTab: repairActiveTab(board, state.activeTab),
          selectedTarget: null,
          draggingMemberId: null,
          exportDateLabel: "",
          saveStatus: "idle",
          saveMessage: null,
          publishMessage: null,
          hasPendingSave: false,
          currentVersion: 0,
          lastSavedSnapshot: null,
          rosterRecord: null,
          loadedRosterDate: null,
        }
      }),
    initializeMissingWeekDraft: (rosterDate) =>
      set((state) => {
        const board = createEmptyGuildWarRosterBoard()
        return {
          board,
          activeTab: repairActiveTab(board, state.activeTab),
          selectedTarget: null,
          draggingMemberId: null,
          exportDateLabel: "",
          saveStatus: "idle",
          saveMessage: null,
          publishMessage: null,
          hasPendingSave: false,
          currentVersion: 0,
          lastSavedSnapshot: createDraftSnapshot({
            board,
            sourceWeekId: state.effectiveSourceWeekId,
            displayDate: "",
          }),
          rosterRecord: null,
          loadedRosterDate: rosterDate,
        }
      }),
    hydrateFromAuthoritativeRecord: ({
      record,
      rosterDate,
      preserveFeedback,
    }) =>
      set((state) => {
        const nextBoard = coerceTeamBoardState(record.draft)
        const nextDisplayDate = record.displayDate ?? ""
        const nextSourceWeekId = record.sourceWeekId ?? ""

        const snapshot = createDraftSnapshot({
          board: nextBoard,
          sourceWeekId: nextSourceWeekId,
          displayDate: nextDisplayDate,
        })

        return {
          board: nextBoard,
          activeTab: repairActiveTab(nextBoard, state.activeTab),
          selectedTarget: null,
          draggingMemberId: null,
          exportDateLabel: nextDisplayDate,
          sourceWeekId: nextSourceWeekId,
          rosterRecord: record,
          loadedRosterDate: rosterDate,
          currentVersion: record.version,
          lastSavedSnapshot: snapshot,
          hasPendingSave: false,
          saveStatus: preserveFeedback ? state.saveStatus : "idle",
          saveMessage: preserveFeedback ? state.saveMessage : null,
          publishMessage: preserveFeedback ? state.publishMessage : null,
          rosterLoadError: null,
        }
      }),
    loadPublishedIntoDraft: () =>
      set((state) => {
        if (!state.rosterRecord?.published) {
          return {}
        }

        const board = coerceTeamBoardState(state.rosterRecord.published)
        return {
          board,
          activeTab: repairActiveTab(board, state.activeTab),
          selectedTarget: null,
          draggingMemberId: null,
          exportDateLabel: state.rosterRecord.publishedDisplayDate ?? "",
          publishMessage: "Published snapshot loaded into draft",
        }
      }),

    pushToast: (toast) => {
      const id = Date.now() + Math.floor(Math.random() * 1000)

      set((state) => ({
        toasts: [...state.toasts, { id, ...toast }],
      }))

      toastTimeouts[id] = setTimeout(() => {
        get().dismissToast(id)
      }, 5000)

      return id
    },
    dismissToast: (toastId) => {
      clearToastTimeout(toastId)
      set((state) => ({
        toasts: state.toasts.filter((toast) => toast.id !== toastId),
      }))
    },
    clearToasts: () => {
      for (const toastId of Object.keys(toastTimeouts)) {
        clearToastTimeout(Number(toastId))
      }

      set({ toasts: [] })
    },
    dispose: () => {
      get().clearToasts()
    },
  }))

  const actions = extractActions(store)

  return { store, actions }
}

export const selectHasUnsavedChanges = (state: TeamBoardPageState) => {
  if (!state.rosterDate) {
    return false
  }

  const snapshot = createDraftSnapshot({
    board: state.board,
    sourceWeekId: state.effectiveSourceWeekId,
    displayDate: state.exportDateLabel.trim(),
  })

  return snapshot !== state.lastSavedSnapshot
}

export const selectSelectedMemberId = (state: TeamBoardPageState) => {
  if (!state.selectedTarget) {
    return null
  }

  return getMemberIdForTarget(state.board, state.selectedTarget)
}

export const selectSelectedMemberPathSnapshot = (state: TeamBoardPageState) => {
  const memberId = selectSelectedMemberId(state)
  if (!memberId) {
    return null
  }

  return state.board.memberPaths[memberId] ?? null
}

export const selectSelectedMemberDisplayNameOverride = (
  state: TeamBoardPageState,
) => {
  const memberId = selectSelectedMemberId(state)
  if (!memberId) {
    return null
  }

  return state.board.memberDisplayNames[memberId] ?? null
}

export const selectAssignedCount = (state: TeamBoardPageState) =>
  new Set(getAssignedMemberIds(state.board)).size

export const selectCommandersCount = (state: TeamBoardPageState) =>
  state.board.commanders.filter(Boolean).length

export const selectHalftimeCount = (state: TeamBoardPageState) =>
  state.board.halftimePerformers.filter(Boolean).length

export const useTeamBoardPageStoreValue = <T>(
  storeApi: StoreApi<TeamBoardPageState>,
  selector: (state: TeamBoardPageState) => T,
) => useStore(storeApi, selector)

const teamBoardRouteApi = getRouteApi("/_authed/_admin/guild-war/team-board")

export const useTeamBoardRouteStore = () =>
  teamBoardRouteApi.useRouteContext({
    select: (context) => context.teamBoardStore,
  })

export const useTeamBoardSelectedTarget = (storeApi: TeamBoardPageStore) =>
  useStore(storeApi.store, (state) => state.selectedTarget)

export const useTeamBoardShowExportPreview = (storeApi: TeamBoardPageStore) =>
  useStore(storeApi.store, (state) => state.showExportPreview)

export const useTeamBoardExportDateLabel = (storeApi: TeamBoardPageStore) =>
  useStore(storeApi.store, (state) => state.exportDateLabel)

export const useTeamBoardHasUnsavedChanges = (storeApi: TeamBoardPageStore) =>
  useStore(storeApi.store, selectHasUnsavedChanges)

export type { SaveStatus, TeamBoardPageState, TeamBoardActiveTab }
